/**
 * API client for the Synapse-AIBeatMapper backend.
 */

import type { Beatmap, BeatmapSummary, BatchUploadResult } from '../types/beatmap';
import { API_BASE } from './apiBase';
import { useAuthStore } from '../store/authStore';

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Merge the caller's headers with the Authorization bearer, if a token is
 *  in the auth store. Exported so places that call `fetch` directly (blob
 *  downloads, media URLs) can attach the same header without depending on
 *  Zustand internals. */
export function authHeaders(extra?: HeadersInit): HeadersInit {
  const token = useAuthStore.getState().token;
  const base: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (!extra) return base;
  // Preserve caller-provided headers (e.g. Content-Type) — Authorization
  // wins only if the caller didn't already set one.
  if (extra instanceof Headers) {
    const merged = new Headers(extra);
    if (token && !merged.has('Authorization')) merged.set('Authorization', `Bearer ${token}`);
    return merged;
  }
  if (Array.isArray(extra)) {
    const has = extra.some(([k]) => k.toLowerCase() === 'authorization');
    return has || !token ? extra : [...extra, ['Authorization', `Bearer ${token}`]];
  }
  return { ...base, ...(extra as Record<string, string>) };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: authHeaders(init?.headers),
    });
  } catch (err: any) {
    // Network-level failure (server down, CORS, timeout, etc.)
    const hint = err?.message?.includes('Failed to fetch')
      ? 'Cannot reach backend server. Make sure the backend is running (python run.py).'
      : err?.message || 'Network error';
    throw new Error(hint);
  }
  if (res.status === 401) {
    // The global fetch interceptor already called logout() on 401 (see
    // src/api/fetchInterceptor.ts); we only surface the error to the caller
    // so upstream UI can show a message.
    throw new Error('Session expired — please sign in again.');
  }
  if (!res.ok) {
    // Try to parse JSON error body (e.g. {error: "...", type: "..."})
    let msg: string;
    try {
      const json = await res.json();
      msg = json.error || json.detail || JSON.stringify(json);
    } catch {
      msg = await res.text().catch(() => `HTTP ${res.status}`);
    }
    throw new Error(msg);
  }
  return res.json();
}

// ── Upload ──────────────────────────────────────────────────────────────────

export async function uploadFiles(
  files: File[],
  password?: string,
  editor?: string,
): Promise<BatchUploadResult> {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  // The archive password is a form field, never a query parameter: a URL is
  // recorded by the access log, the browser history and the Referer header
  // (SEC-0016). `editor` is a tag, not a credential, so it stays in the query.
  if (password) form.append('password', password);

  const params = new URLSearchParams();
  if (editor) params.set('editor', editor);   // tag uploads into this editor's library
  const query = params.toString() ? `?${params.toString()}` : '';
  return request<BatchUploadResult>(`/api/upload${query}`, {
    method: 'POST',
    body: form,
  });
}

// ── Create New Beatmap ───────────────────────────────────────────────────────

export async function createBeatmap(
  audio: File,
  name: string,
  author: string,
  beatmapper: string,
  bpm: number | null | undefined,
  offset: number,
): Promise<Beatmap> {
  const form = new FormData();
  form.append('audio', audio);
  const params = new URLSearchParams({
    name,
    author,
    beatmapper,
    offset: String(offset),
  });
  // No BPM means "detect it from the audio"; a number — 120 included — is the mapper's own and is kept.
  if (bpm != null) params.set('bpm', String(bpm));
  return request<Beatmap>(`/api/create-beatmap?${params}`, {
    method: 'POST',
    body: form,
  });
}

// ── Beatmaps ────────────────────────────────────────────────────────────────

/** List the library for one editor (`'synth-rider'` | `'console'`). Omit `editor`
 *  to list everything. Synth VR also receives untagged (legacy shared) maps. */
export async function listBeatmaps(editor?: string): Promise<BeatmapSummary[]> {
  const q = editor ? `?editor=${encodeURIComponent(editor)}` : '';
  return request<BeatmapSummary[]>(`/api/beatmaps${q}`);
}

export async function getBeatmap(id: string): Promise<Beatmap> {
  return request<Beatmap>(`/api/beatmaps/${id}`);
}

export async function deleteBeatmap(id: string): Promise<void> {
  await request(`/api/beatmaps/${id}`, { method: 'DELETE' });
}

/** Publish/unpublish a beatmap (owner-only; the backend 403s otherwise). */
export async function setBeatmapVisibility(
  id: string,
  isPublic: boolean,
): Promise<{ success: boolean; id: string; visibility: 'private' | 'public' }> {
  return request(`/api/beatmaps/${id}/visibility`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ public: isPublic }),
  });
}

/** Ask the (local) backend to open the folder containing this map's .synth file
 *  in the OS file explorer, highlighting the file where supported. Only works
 *  for maps loaded from a folder scan (they have a source path on disk). */
export async function revealBeatmap(id: string): Promise<{ revealed: boolean; path: string }> {
  return request(`/api/beatmaps/${id}/reveal`, { method: 'POST' });
}

/** The folder the backend scans for .synth files (configurable, single folder). */
export interface ScanFolderInfo {
  path: string | null;     // configured custom folder, or null = using defaults
  is_custom: boolean;
  exists: boolean;
  defaults: string[];      // built-in default scan locations
  loaded: number;          // maps currently in the store
}

export async function getScanFolder(editor?: string): Promise<ScanFolderInfo> {
  const q = editor ? `?editor=${encodeURIComponent(editor)}` : '';
  return request<ScanFolderInfo>(`/api/beatmaps/scan-folder${q}`);
}

/** Set (or clear, with '') a scan folder; the backend re-scans in the background.
 *  With `editor`, sets that editor's own folder (per-editor library). */
export async function setScanFolder(path: string, editor?: string): Promise<{ path: string | null; rescanning: boolean }> {
  return request('/api/beatmaps/scan-folder', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, editor }),
  });
}

/** Attach the current session's JWT to a GET media URL so `<audio>`/`<img>`
 *  tags can fetch protected endpoints without an Authorization header. */
function withToken(url: string): string {
  const token = useAuthStore.getState().token;
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

export function getAudioUrl(id: string): string {
  return withToken(`${API_BASE}/api/beatmaps/${id}/audio`);
}

export function getArtworkUrl(id: string): string {
  return withToken(`${API_BASE}/api/beatmaps/${id}/artwork`);
}

// ── Export ───────────────────────────────────────────────────────────────────

export async function exportBeatmap(id: string, password?: string): Promise<Blob> {
  // The password goes in the body (SEC-0016). Without one there is no body at
  // all, which is what the endpoint expects for a plain re-export.
  const res = await fetch(`${API_BASE}/api/export/${id}`, {
    method: 'POST',
    ...(password
      ? { headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify({ password }) }
      : { headers: authHeaders() }),
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}

export async function exportEditedBeatmap(data: Record<string, unknown>, password?: string): Promise<Blob> {
  // Folded into the JSON body this request already sends (SEC-0016).
  const res = await fetch(`${API_BASE}/api/export`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(password ? { ...data, password } : data),
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}

// ── Download helper ─────────────────────────────────────────────────────────

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
